import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import DashboardHeaderContainer from '../../../sharable/components/DashboardHeaderContainer';
import Select from '../../../sharable/components/Select';
import ButtonSmall from '../../../sharable/components/ButtonSmall';
import ArticlesTagsFilter from '../articlesTags/ArticlesTagsFilter';
import ArticlesSearch from '../ArticlesSearch';
import Icon from 'utils/components/icon';
import {
  changeArticlesAppliedFilterTags,
  changeArticlesSearchBy,
  changeArticlesSearchValue,
  changeArticlesSortBy,
  fetchArticlesFilterTags
} from '../../reducers/loadArticlesSlice';
import { ArticlesSearchByParams, ArticlesSortByParams } from '../../../../../../constants';
import styles from './ArticlesHeader.module.scss';
import { useTenantConfig } from '../../../../../tenant-config/useTenantConfig';
import { TABS_V2_API_ENABLED } from '../../../../../tenant-config/tenantConfigConstants';
import {
  selectArticlesAppliedFilterTags,
  selectArticlesSearchValue,
  selectArticlesSortBy
} from '../../reducers/articlesSelectors';

const SORT_BY_OPTIONS = [
  { label: 'Newest', value: ArticlesSortByParams.Newest },
  { label: 'Oldest', value: ArticlesSortByParams.Oldest },
  { label: 'Pending', value: ArticlesSortByParams.Pending },
  { label: 'Rejected', value: ArticlesSortByParams.Rejected },
  { label: 'Total', value: ArticlesSortByParams.All }
];

const ArticlesHeader = () => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  const [isTabsV2Enabled] = useTenantConfig(TABS_V2_API_ENABLED);

  const sortBy = useSelector(selectArticlesSortBy);
  const searchValue = useSelector(selectArticlesSearchValue);
  const appliedFilterTags = useSelector(selectArticlesAppliedFilterTags);

  const dispatch = useDispatch();

  const selectedSearchByOption =
    SORT_BY_OPTIONS.find(({ value }) => value === sortBy) ?? SORT_BY_OPTIONS[0];
  const isClearFilters =
    !isSearchExpanded &&
    (sortBy !== ArticlesSortByParams.Newest || searchValue || !!appliedFilterTags?.length);

  const onSortByChange = useCallback(
    ({ value }: { label: string; value: ArticlesSortByParams }) =>
      dispatch(changeArticlesSortBy(value)),
    [dispatch]
  );

  useEffect(() => {
    if (isTabsV2Enabled !== undefined) {
      dispatch(
        fetchArticlesFilterTags({
          offset: 0,
          search: '',
          useV2: isTabsV2Enabled
        })
      );
    }
  }, [dispatch, isTabsV2Enabled]);

  const onClearFilters = () => {
    dispatch(changeArticlesSortBy(ArticlesSortByParams.Newest));
    dispatch(changeArticlesSearchBy(ArticlesSearchByParams.Title));
    dispatch(changeArticlesSearchValue(''));
    dispatch(changeArticlesAppliedFilterTags([]));
  };

  return (
    <DashboardHeaderContainer>
      {!isSearchExpanded && (
        <>
          <ArticlesTagsFilter isTabsV2Enabled={isTabsV2Enabled} />
          {!searchValue && (
            <Select
              isBorderless
              value={selectedSearchByOption}
              options={SORT_BY_OPTIONS}
              controlClassName={styles.articlesSortByControl}
              icon={<Icon className={styles.articlesSortByIcon} name="sort" />}
              onChange={onSortByChange}
            />
          )}
        </>
      )}

      <ArticlesSearch
        className={cx(styles.articlesSearch, { [styles.articlesSearchExpanded]: isSearchExpanded })}
        onExpand={() => setIsSearchExpanded(true)}
        onCollapse={() => setIsSearchExpanded(false)}
      />

      {isClearFilters && (
        <ButtonSmall className={styles.clearButton} onClick={onClearFilters}>
          Clear Filters
        </ButtonSmall>
      )}
    </DashboardHeaderContainer>
  );
};

export default ArticlesHeader;
